// @ts-nocheck
import { useState, useEffect } from 'react'
import { X, Plus, Trash2, AlertTriangle, Save } from 'lucide-react'

// 利用可能な番号キー
const AVAILABLE_DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '*', '#']

// アクションのラベル
const ACTION_LABELS = {
  play_message: 'メッセージ再生',
  go_to_ivr: 'IVRメニューへ移動',
  route_to_group: 'グループへ転送',
  route_to_queue: 'キューへ転送',
}

// 音声オプション
const VOICE_OPTIONS = {
  'ja-JP': [
    { value: 'alice', label: 'Alice (女性)' },
    { value: 'Polly.Mizuki', label: 'Mizuki (女性)' },
    { value: 'Polly.Takumi', label: 'Takumi (男性)' },
  ],
  'en-US': [
    { value: 'alice', label: 'Alice (女性)' },
    { value: 'Polly.Joanna', label: 'Joanna (女性)' },
    { value: 'Polly.Matthew', label: 'Matthew (男性)' },
  ],
}

// デフォルトのプロンプト設定
function createDefaultPrompt() {
  return {
    type: 'tts',
    ttsText: '',
    ttsLanguage: 'ja-JP',
    ttsVoice: 'alice',
    audioUrl: '',
  }
}

// デフォルトのオプション設定
function createDefaultOption(digit) {
  return {
    digit,
    label: '',
    action: 'route_to_queue',
    message: createDefaultPrompt(),
    afterMessage: 'hangup',
    targetIvrMenuId: '',
    groupId: '',
  }
}

export default function IvrMenuEditor({ menu, groups, allMenus, onSave, onClose }) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  // フォームの状態
  const [name, setName] = useState('')
  const [prompt, setPrompt] = useState(createDefaultPrompt())
  const [options, setOptions] = useState([])
  const [timeoutAction, setTimeoutAction] = useState('replay')
  const [invalidAction, setInvalidAction] = useState('replay')
  const [maxAttempts, setMaxAttempts] = useState(3)

  // 編集モードの場合、既存データで初期化
  useEffect(() => {
    if (menu) {
      setName(menu.name)
      setPrompt({
        type: menu.prompt.type,
        ttsText: menu.prompt.ttsText,
        ttsLanguage: menu.prompt.ttsLanguage,
        ttsVoice: menu.prompt.ttsVoice,
        audioUrl: menu.prompt.audioUrl,
      })

      // Record<string, IvrMenuOption> を配列に変換
      const optionEntries = Object.entries(menu.options).map(([digit, opt]) => ({
        digit,
        label: opt.label,
        action: opt.action,
        message: opt.message ? { ...opt.message } : createDefaultPrompt(),
        afterMessage: opt.afterMessage,
        targetIvrMenuId: opt.targetIvrMenuId,
        groupId: opt.groupId,
      }))
      setOptions(optionEntries)

      setTimeoutAction(menu.timeoutAction ?? 'replay')
      setInvalidAction(menu.invalidAction ?? 'replay')
      setMaxAttempts(menu.maxAttempts ?? 3)
    }
  }, [menu])

  // 使用済み番号キーの一覧
  const usedDigits = options.map((opt) => opt.digit)

  // 利用可能な番号キー（まだ割り当てていないもの）
  const availableDigitsForNew = AVAILABLE_DIGITS.filter((d) => !usedDigits.includes(d))

  // オプション追加
  const handleAddOption = () => {
    if (availableDigitsForNew.length === 0) return
    const nextDigit = availableDigitsForNew[0]
    setOptions((prev) => [...prev, createDefaultOption(nextDigit)])
  }

  // オプション削除
  const handleRemoveOption = (index) => {
    setOptions((prev) => prev.filter((_, i) => i !== index))
  }

  // オプションフィールド更新
  const handleOptionChange = (index, field, value) => {
    setOptions((prev) => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }
      return updated
    })
  }

  // オプション内のメッセージプロンプト更新
  const handleOptionMessageChange = (index, field, value) => {
    setOptions((prev) => {
      const updated = [...prev]
      updated[index] = {
        ...updated[index],
        message: { ...updated[index].message, [field]: value },
      }
      return updated
    })
  }

  // ループ検出: 自分自身を参照しているか
  const detectSelfReference = (targetIvrMenuId) => {
    if (!menu) return false
    return targetIvrMenuId === menu.ivrMenuId
  }

  // バリデーション
  const validate = () => {
    if (!name.trim()) {
      return 'メニュー名を入力してください'
    }

    if (prompt.type === 'tts' && !prompt.ttsText?.trim()) {
      return 'プロンプトのテキストを入力してください'
    }

    if (prompt.type === 'audio_url' && !prompt.audioUrl?.trim()) {
      return 'プロンプトの音声URLを入力してください'
    }

    for (const opt of options) {
      if (!opt.label.trim()) {
        return `番号「${opt.digit}」のラベルを入力してください`
      }
      if (opt.action === 'play_message') {
        if (opt.message.type === 'tts' && !opt.message.ttsText?.trim()) {
          return `番号「${opt.digit}」のメッセージテキストを入力してください`
        }
        if (opt.message.type === 'audio_url' && !opt.message.audioUrl?.trim()) {
          return `番号「${opt.digit}」の音声URLを入力してください`
        }
      }
      if (opt.action === 'go_to_ivr' && !opt.targetIvrMenuId) {
        return `番号「${opt.digit}」の遷移先IVRメニューを選択してください`
      }
      if (opt.action === 'route_to_group' && !opt.groupId) {
        return `番号「${opt.digit}」の転送先グループを選択してください`
      }
    }

    return null
  }

  // 保存
  const handleSave = async () => {
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }

    // オプション配列を Record<string, IvrMenuOption> に変換
    const optionsRecord = {}
    for (const opt of options) {
      const optionData = {
        label: opt.label,
        action: opt.action,
      }

      if (opt.action === 'play_message') {
        optionData.message = {
          type: opt.message.type,
          ...(opt.message.type === 'tts'
            ? {
                ttsText: opt.message.ttsText,
                ttsLanguage: opt.message.ttsLanguage,
                ttsVoice: opt.message.ttsVoice,
              }
            : {
                audioUrl: opt.message.audioUrl,
              }),
        }
        optionData.afterMessage = opt.afterMessage
      }

      if (opt.action === 'go_to_ivr') {
        optionData.targetIvrMenuId = opt.targetIvrMenuId
      }

      if (opt.action === 'route_to_group') {
        optionData.groupId = opt.groupId
      }

      optionsRecord[opt.digit] = optionData
    }

    const data = {
      name: name.trim(),
      prompt: {
        type: prompt.type,
        ...(prompt.type === 'tts'
          ? {
              ttsText: prompt.ttsText,
              ttsLanguage: prompt.ttsLanguage,
              ttsVoice: prompt.ttsVoice,
            }
          : {
              audioUrl: prompt.audioUrl,
            }),
      },
      options: optionsRecord,
      timeoutAction,
      invalidAction,
      maxAttempts,
    }

    try {
      setSaving(true)
      setError(null)
      await onSave(data)
    } catch (err) {
      console.error('IVRメニュー保存エラー:', err)
      setError(err.message ?? '保存に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  // 現在のメニューを除外したIVRメニュー一覧
  const selectableMenus = menu
    ? allMenus.filter((m) => m.ivrMenuId !== menu.ivrMenuId)
    : allMenus

  // プロンプト設定UIのレンダリング（メインプロンプトとオプション内で共通利用）
  const renderPromptEditor = (config, onChange, prefix) => {
    return (
      <div className="space-y-3">
        {/* タイプ切り替え */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onChange('type', 'tts')}
            className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
              config.type === 'tts'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-400'
            }`}
          >
            TTS
          </button>
          <button
            type="button"
            onClick={() => onChange('type', 'audio_url')}
            className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
              config.type === 'audio_url'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-400'
            }`}
          >
            音声ファイル
          </button>
        </div>

        {config.type === 'tts' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                テキスト <span className="text-red-500">*</span>
              </label>
              <textarea
                value={config.ttsText ?? ''}
                onChange={(e) => onChange('ttsText', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="読み上げるテキストを入力してください"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  言語
                </label>
                <select
                  value={config.ttsLanguage ?? 'ja-JP'}
                  onChange={(e) => {
                    onChange('ttsLanguage', e.target.value)
                    const voices = VOICE_OPTIONS[e.target.value]
                    if (voices && voices.length > 0) {
                      onChange('ttsVoice', voices[0].value)
                    }
                  }}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="ja-JP">日本語</option>
                  <option value="en-US">英語 (米国)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  音声
                </label>
                <select
                  value={config.ttsVoice ?? 'alice'}
                  onChange={(e) => onChange('ttsVoice', e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  {(VOICE_OPTIONS[config.ttsLanguage ?? 'ja-JP'] ?? VOICE_OPTIONS['ja-JP']).map(
                    (voice) => (
                      <option key={voice.value} value={voice.value}>
                        {voice.label}
                      </option>
                    )
                  )}
                </select>
              </div>
            </div>
          </div>
        )}

        {config.type === 'audio_url' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              音声URL <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={config.audioUrl ?? ''}
              onChange={(e) => onChange('audioUrl', e.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              placeholder="https://example.com/audio.mp3"
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* ヘッダー */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {menu ? 'IVRメニュー編集' : 'IVRメニュー作成'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* スクロール可能なコンテンツ */}
        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {/* エラー表示 */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-800 dark:text-red-200">{error}</span>
            </div>
          )}

          {/* メニュー名 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              メニュー名 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="例: メインメニュー"
            />
          </div>

          {/* プロンプト設定 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              プロンプト設定
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              発信者に再生されるガイダンスメッセージを設定します
            </p>
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              {renderPromptEditor(prompt, (field, value) => {
                setPrompt((prev) => ({ ...prev, [field]: value }))
              })}
            </div>
          </div>

          {/* 番号オプション設定 */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                番号オプション設定
              </label>
              <button
                type="button"
                onClick={handleAddOption}
                disabled={availableDigitsForNew.length === 0}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
                オプション追加
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              発信者が押す番号キーとそのアクションを設定します
            </p>

            {options.length === 0 && (
              <div className="text-center py-8 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  オプションがありません。「オプション追加」ボタンで番号キーを追加してください。
                </p>
              </div>
            )}

            <div className="space-y-4">
              {options.map((opt, index) => (
                <div
                  key={`${opt.digit}-${index}`}
                  className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
                >
                  {/* オプションヘッダー: 番号キー・ラベル・削除ボタン */}
                  <div className="flex items-start gap-3 mb-3">
                    {/* 番号キー選択 */}
                    <div className="flex-shrink-0">
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        番号
                      </label>
                      <select
                        value={opt.digit}
                        onChange={(e) => handleOptionChange(index, 'digit', e.target.value)}
                        className="w-16 px-2 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-center font-mono"
                      >
                        <option value={opt.digit}>{opt.digit}</option>
                        {AVAILABLE_DIGITS.filter(
                          (d) => d === opt.digit || !usedDigits.includes(d)
                        ).map((d) => (
                          d !== opt.digit && (
                            <option key={d} value={d}>{d}</option>
                          )
                        ))}
                      </select>
                    </div>

                    {/* ラベル */}
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        ラベル <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={opt.label}
                        onChange={(e) => handleOptionChange(index, 'label', e.target.value)}
                        className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="例: 営業部門"
                      />
                    </div>

                    {/* アクション選択 */}
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        アクション
                      </label>
                      <select
                        value={opt.action}
                        onChange={(e) => handleOptionChange(index, 'action', e.target.value)}
                        className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      >
                        {Object.entries(ACTION_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>

                    {/* 削除ボタン */}
                    <div className="flex-shrink-0 pt-5">
                      <button
                        type="button"
                        onClick={() => handleRemoveOption(index)}
                        className="p-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="オプションを削除"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* アクション固有のフィールド */}
                  {opt.action === 'play_message' && (
                    <div className="mt-3 pl-4 border-l-2 border-gray-300 dark:border-gray-600 space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                          再生メッセージ
                        </label>
                        {renderPromptEditor(
                          opt.message,
                          (field, value) => handleOptionMessageChange(index, field, value)
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                          メッセージ後の動作
                        </label>
                        <select
                          value={opt.afterMessage ?? 'hangup'}
                          onChange={(e) => handleOptionChange(index, 'afterMessage', e.target.value)}
                          className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        >
                          <option value="hangup">切断する</option>
                          <option value="return">メニューに戻る</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {opt.action === 'go_to_ivr' && (
                    <div className="mt-3 pl-4 border-l-2 border-gray-300 dark:border-gray-600">
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        IVRメニュー選択 <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={opt.targetIvrMenuId ?? ''}
                        onChange={(e) => handleOptionChange(index, 'targetIvrMenuId', e.target.value)}
                        className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      >
                        <option value="">-- 選択してください --</option>
                        {selectableMenus.map((m) => (
                          <option key={m.ivrMenuId} value={m.ivrMenuId}>{m.name}</option>
                        ))}
                        {/* 自分自身も選択肢に含める（警告表示のため） */}
                        {menu && (
                          <option value={menu.ivrMenuId}>{menu.name} (現在のメニュー)</option>
                        )}
                      </select>

                      {/* ループ検出警告 */}
                      {detectSelfReference(opt.targetIvrMenuId) && (
                        <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                          <span className="text-xs text-yellow-800 dark:text-yellow-200">
                            このオプションは現在編集中のメニュー自身を参照しています。ループが発生する可能性があります。
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {opt.action === 'route_to_group' && (
                    <div className="mt-3 pl-4 border-l-2 border-gray-300 dark:border-gray-600">
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        グループ選択 <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={opt.groupId ?? ''}
                        onChange={(e) => handleOptionChange(index, 'groupId', e.target.value)}
                        className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      >
                        <option value="">-- 選択してください --</option>
                        {groups.map((g) => (
                          <option key={g.groupId} value={g.groupId}>{g.groupName}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {opt.action === 'route_to_queue' && (
                    <div className="mt-3 pl-4 border-l-2 border-gray-300 dark:border-gray-600">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        デフォルトのキューに転送されます
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* タイムアウト・無効入力・リトライ設定 */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                タイムアウト動作
              </label>
              <select
                value={timeoutAction}
                onChange={(e) => setTimeoutAction(e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="replay">もう一度再生</option>
                <option value="hangup">切断する</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                無効入力動作
              </label>
              <select
                value={invalidAction}
                onChange={(e) => setInvalidAction(e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="replay">もう一度再生</option>
                <option value="hangup">切断する</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                最大リトライ回数
              </label>
              <input
                type="number"
                min={1}
                max={10}
                value={maxAttempts}
                onChange={(e) => setMaxAttempts(parseInt(e.target.value, 10))}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>
        </div>

        {/* フッター: 保存・キャンセルボタン */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                保存中...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                保存
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
